import type { SerializedStyles } from '@emotion/react';
import { css } from '@emotion/react';
import * as tokens from '@tokens';

const inputBox = ({ isInvalid }: { isInvalid: boolean }): string => `
  width: 100%;
  background-color: ${tokens.color.ui_background};
  ${tokens.text('body2')};
  border-radius: ${tokens.radius.m};
  color: ${isInvalid ? tokens.color.text_danger : tokens.color.text_default};
  box-shadow: inset 0 0 0 1px ${
    isInvalid ? tokens.color.ui_border_danger : tokens.color.ui_border
  }
`;

const inputOverrides = (isInvalid = false): string => `
  outline: none;
  border: none;
  ${tokens.text('body2')};

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active
  {
    -webkit-text-fill-color: ${
      isInvalid ? tokens.color.text_danger : tokens.color.text_default
    };
  }

  &::placeholder {
    color: ${tokens.color.text_disabled};
    -webkit-text-fill-color: ${tokens.color.text_disabled};
  }

  &:placeholder-shown {
    text-overflow: ellipsis;
  }
`;

const inputBoxHover = ({ isInvalid }: { isInvalid: boolean }): string => `
box-shadow: inset 0 0 0 1px ${
  isInvalid ? tokens.color.ui_border_danger : tokens.color.ui_border_alt
}
`;

const inputBoxFocus = (): string => `
  ${`${tokens.focus()}, inset 0 0 0 1px ${tokens.color.ui_border_alt}`};
`;

const inputBoxDisabled = (): string => `
  background-color: ${tokens.color.ui_disabled};
  ${tokens.elevation(0)};
  color: ${tokens.color.text_disabled};

  & input {
   cursor: not-allowed;
  }
`;

const inputBoxReadonly = (): string => `
  ${tokens.elevation(0)};
  color: ${tokens.color.text_default};
  background-color: ${tokens.color.ui_disabled};
  box-shadow: inset 0 0 0 1px ${tokens.color.ui_border}
`;

export const baseInputGhostContainer = ({
  isInvalid,
  isDisabled,
  isReadonly,
}: {
  isInvalid: boolean;
  isDisabled: boolean;
  isReadonly: boolean;
}): SerializedStyles => css`
  ${inputBox({ isInvalid })};
  ${isReadonly ? inputBoxReadonly() : ''};
  ${isDisabled
    ? inputBoxDisabled()
    : `
  &:hover {
    ${inputBoxHover({ isInvalid })};
  }

  &:focus-within {
    ${inputBoxFocus()};
  }
  `};
`;

export const baseInputGhost = (isInvalid = false): SerializedStyles => css`
  ${inputOverrides(isInvalid)};
  background-clip: text !important; // working around browser autofill background styles
  color: inherit;
  background: none;
`;

export const baseInput = ({
  isInvalid,
  readOnly,
}: {
  isInvalid: boolean;
  readOnly: boolean;
}): SerializedStyles => css`
  ${inputBox({ isInvalid })};
  ${inputOverrides(isInvalid)};

  &:read-only {
    ${readOnly && inputBoxReadonly()};
  }

  &:hover {
    ${inputBoxHover({ isInvalid })};
  }

  &:focus {
    ${inputBoxFocus()};
  }

  &[disabled] {
    ${inputBoxDisabled()};
  }
`;
